import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Cog6ToothIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  SignalIcon,
  CloudIcon,
  CpuChipIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  PencilIcon,
  XMarkIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface IntegrationStatus {
  id: string;
  name: string;
  description: string;
  configured: boolean;
  connected: boolean;
  lastTested?: string;
  error?: string;
  details?: Record<string, unknown>;
}

interface IntegrationConfig {
  microsoft: {
    clientId: string | null;
    tenantId: string | null;
    clientSecret: string | null;
    redirectUri: string;
  };
  unleashed: {
    apiId: string | null;
    apiKey: string | null;
  };
  openai: {
    apiKey: string | null;
    model: string;
  };
  database: {
    host: string;
    database: string;
    port: number;
  };
}

interface TestResult {
  success: boolean;
  message: string;
  responseTime?: number;
  details?: Record<string, unknown>;
}

interface SystemStatus {
  status: string;
  uptime: { seconds: number; formatted: string };
  memory: { heapUsed: number; heapTotal: number; rss: number };
  node: string;
  pid: number;
}

interface EditModalProps {
  integration: string;
  fields: { key: string; label: string; type?: string; placeholder?: string }[];
  currentValues: Record<string, string | null>;
  onSave: (values: Record<string, string>) => void;
  onClose: () => void;
  isLoading: boolean;
}

function EditModal({ integration, fields, currentValues, onSave, onClose, isLoading }: EditModalProps) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach(f => {
      initial[f.key] = currentValues[f.key] || '';
    });
    return initial;
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(values);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Edit {integration} Configuration</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {fields.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <input
                type={field.type || 'text'}
                value={values[field.key]}
                onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              {currentValues[field.key] && (
                <p className="mt-1 text-xs text-gray-500">
                  Current: {currentValues[field.key]}
                </p>
              )}
            </div>
          ))}

          <div className="pt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        <div className="px-6 py-3 bg-amber-50 border-t border-amber-200 rounded-b-lg">
          <p className="text-xs text-amber-700">
            After saving, click "Restart Service" to apply changes.
          </p>
        </div>
      </div>
    </div>
  );
}

const integrationIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  microsoft: CloudIcon,
  unleashed: SignalIcon,
  openai: CpuChipIcon,
  database: CircleStackIcon,
};

const integrationFields: Record<string, { key: string; label: string; type?: string }[]> = {
  microsoft: [
    { key: 'clientId', label: 'Client ID' },
    { key: 'tenantId', label: 'Tenant ID' },
    { key: 'clientSecret', label: 'Client Secret', type: 'password' },
  ],
  unleashed: [
    { key: 'apiId', label: 'API ID' },
    { key: 'apiKey', label: 'API Key', type: 'password' },
  ],
  openai: [
    { key: 'apiKey', label: 'API Key', type: 'password' },
  ],
};

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [testingId, setTestingId] = useState<string | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState(false);

  // Handle OAuth callback messages
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success');
    const oauthError = searchParams.get('oauth_error');

    if (oauthSuccess === 'true') {
      setShowSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['integrations-status'] });
      setSearchParams({});
      setTimeout(() => setShowSuccess(false), 5000);
    }

    if (oauthError) {
      setShowError(decodeURIComponent(oauthError));
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, queryClient]);

  // Fetch integrations status
  const { data: integrations, isLoading: loadingIntegrations } = useQuery<IntegrationStatus[]>({
    queryKey: ['integrations-status'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/integrations/status`);
      if (!res.ok) throw new Error('Failed to fetch status');
      const json = await res.json();
      return json.data;
    },
    refetchInterval: 30000,
  });

  // Fetch integrations config
  const { data: config, refetch: refetchConfig } = useQuery<IntegrationConfig>({
    queryKey: ['integrations-config'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/integrations/config`);
      if (!res.ok) throw new Error('Failed to fetch config');
      const json = await res.json();
      return json.data;
    },
  });

  // Fetch system status
  const { data: systemStatus } = useQuery<SystemStatus>({
    queryKey: ['system-status'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/system/status`);
      if (!res.ok) throw new Error('Failed to fetch status');
      const json = await res.json();
      return json.data;
    },
    refetchInterval: 10000,
  });

  // Microsoft connect mutation
  const connectMicrosoft = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/api/auth/microsoft/connect`);
      if (!res.ok) throw new Error('Failed to start connection');
      const json = await res.json();
      return json.data;
    },
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: (error) => {
      setShowError(error instanceof Error ? error.message : 'Failed to connect');
    },
  });

  // Microsoft disconnect mutation
  const disconnectMicrosoft = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/api/auth/microsoft/disconnect`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to disconnect');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations-status'] });
    },
  });

  // Test integration mutation
  const testIntegration = useMutation({
    mutationFn: async (integrationId: string) => {
      setTestingId(integrationId);
      const res = await fetch(`${API_BASE}/api/integrations/test/${integrationId}`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Test failed');
      const json = await res.json();
      return { integrationId, result: json.data as TestResult };
    },
    onSuccess: ({ integrationId, result }) => {
      setTestResults(prev => ({ ...prev, [integrationId]: result }));
      setTestingId(null);
    },
    onError: (error, integrationId) => {
      setTestResults(prev => ({
        ...prev,
        [integrationId]: {
          success: false,
          message: error instanceof Error ? error.message : 'Test failed',
        },
      }));
      setTestingId(null);
    },
  });

  // Update config mutation
  const updateConfig = useMutation({
    mutationFn: async ({ integration, values }: { integration: string; values: Record<string, string> }) => {
      const res = await fetch(`${API_BASE}/api/system/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integration, values }),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to update config');
      }
      return res.json();
    },
    onSuccess: () => {
      setEditingIntegration(null);
      setPendingChanges(true);
      refetchConfig();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    },
    onError: (error) => {
      setShowError(error instanceof Error ? error.message : 'Failed to update config');
    },
  });

  // Restart service mutation
  const restartService = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/api/system/restart`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to restart');
      return res.json();
    },
    onSuccess: () => {
      setPendingChanges(false);
      // Show restarting message and wait for reconnection
      setShowSuccess(false);
      setShowError(null);
    },
  });

  // Test all integrations
  const testAllIntegrations = async () => {
    if (!integrations) return;
    for (const integration of integrations) {
      await testIntegration.mutateAsync(integration.id);
    }
  };

  const getStatusColor = (integration: IntegrationStatus) => {
    if (!integration.configured) return 'text-gray-400';
    if (integration.connected) return 'text-green-500';
    return 'text-amber-500';
  };

  const getStatusText = (integration: IntegrationStatus) => {
    if (!integration.configured) return 'Not Configured';
    if (integration.connected) return 'Connected';
    return 'Disconnected';
  };

  const getConfigValues = (integrationId: string): Record<string, string | null> => {
    if (!config) return {};
    switch (integrationId) {
      case 'microsoft':
        return {
          clientId: config.microsoft.clientId,
          tenantId: config.microsoft.tenantId,
          clientSecret: config.microsoft.clientSecret,
        };
      case 'unleashed':
        return {
          apiId: config.unleashed.apiId,
          apiKey: config.unleashed.apiKey,
        };
      case 'openai':
        return {
          apiKey: config.openai.apiKey,
        };
      default:
        return {};
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Cog6ToothIcon className="h-8 w-8 text-gray-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-500">Manage integrations and system configuration</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={testAllIntegrations}
            disabled={testingId !== null}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${testingId ? 'animate-spin' : ''}`} />
            Test All
          </button>
          <button
            onClick={() => restartService.mutate()}
            disabled={restartService.isPending}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${
              pendingChanges
                ? 'bg-amber-600 text-white hover:bg-amber-700'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            } disabled:opacity-50`}
          >
            <ArrowPathIcon className={`h-4 w-4 ${restartService.isPending ? 'animate-spin' : ''}`} />
            {restartService.isPending ? 'Restarting...' : 'Restart Service'}
          </button>
        </div>
      </div>

      {/* System Status Bar */}
      {systemStatus && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <ServerIcon className="h-5 w-5 text-gray-500" />
              <span className="font-medium text-gray-700">Service Status:</span>
              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                Running
              </span>
            </div>
            <div className="text-gray-600">
              Uptime: <span className="font-medium">{systemStatus.uptime.formatted}</span>
            </div>
            <div className="text-gray-600">
              Memory: <span className="font-medium">{systemStatus.memory.heapUsed}MB / {systemStatus.memory.heapTotal}MB</span>
            </div>
            <div className="text-gray-600">
              PID: <span className="font-medium">{systemStatus.pid}</span>
            </div>
          </div>
        </div>
      )}

      {/* Pending Changes Warning */}
      {pendingChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Configuration changes pending</p>
              <p className="text-sm text-amber-700">Click "Restart Service" to apply your changes.</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="rounded-md bg-green-50 p-4 border border-green-200">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <p className="ml-3 text-sm font-medium text-green-800">
              {pendingChanges ? 'Configuration updated! Restart service to apply.' : 'Operation completed successfully!'}
            </p>
          </div>
        </div>
      )}

      {/* Restarting Message */}
      {restartService.isPending && (
        <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />
            <p className="text-sm font-medium text-blue-800">
              Service is restarting... The page will reconnect automatically.
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="rounded-md bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-red-800">{showError}</p>
            </div>
            <button onClick={() => setShowError(null)} className="text-red-500 hover:text-red-700">
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Integrations Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {loadingIntegrations ? (
          <div className="col-span-2 text-center py-8 text-gray-500">Loading integrations...</div>
        ) : (
          integrations?.map((integration) => {
            const Icon = integrationIcons[integration.id] || Cog6ToothIcon;
            const testResult = testResults[integration.id];
            const canEdit = integrationFields[integration.id];

            return (
              <div key={integration.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${integration.connected ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Icon className={`h-6 w-6 ${getStatusColor(integration)}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{integration.name}</h3>
                        <p className="text-sm text-gray-500">{integration.description}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      integration.connected
                        ? 'bg-green-100 text-green-800'
                        : integration.configured
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {getStatusText(integration)}
                    </span>
                  </div>
                </div>

                <div className="px-4 py-5 sm:p-6 space-y-4">
                  {/* Config Details */}
                  {config && (
                    <div className="text-xs text-gray-500 space-y-1">
                      {integration.id === 'microsoft' && config.microsoft && (
                        <>
                          <p><span className="font-medium">Client ID:</span> {config.microsoft.clientId || 'Not set'}</p>
                          <p><span className="font-medium">Tenant ID:</span> {config.microsoft.tenantId || 'Not set'}</p>
                          <p><span className="font-medium">Secret:</span> {config.microsoft.clientSecret || 'Not set'}</p>
                          {integration.details?.userEmail && (
                            <p><span className="font-medium">Account:</span> {String(integration.details.userEmail)}</p>
                          )}
                        </>
                      )}
                      {integration.id === 'unleashed' && config.unleashed && (
                        <>
                          <p><span className="font-medium">API ID:</span> {config.unleashed.apiId || 'Not set'}</p>
                          <p><span className="font-medium">API Key:</span> {config.unleashed.apiKey || 'Not set'}</p>
                        </>
                      )}
                      {integration.id === 'openai' && config.openai && (
                        <>
                          <p><span className="font-medium">API Key:</span> {config.openai.apiKey || 'Not set'}</p>
                          <p><span className="font-medium">Model:</span> {config.openai.model}</p>
                        </>
                      )}
                      {integration.id === 'database' && config.database && (
                        <>
                          <p><span className="font-medium">Host:</span> {config.database.host}</p>
                          <p><span className="font-medium">Database:</span> {config.database.database}</p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Test Result */}
                  {testResult && (
                    <div className={`p-3 rounded-md text-sm ${
                      testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                    }`}>
                      <div className="flex items-center gap-2">
                        {testResult.success ? (
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 text-red-500" />
                        )}
                        <span>{testResult.message}</span>
                        {testResult.responseTime && (
                          <span className="text-xs opacity-75">({testResult.responseTime}ms)</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2">
                    <button
                      onClick={() => testIntegration.mutate(integration.id)}
                      disabled={testingId === integration.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ArrowPathIcon className={`h-3.5 w-3.5 ${testingId === integration.id ? 'animate-spin' : ''}`} />
                      Test
                    </button>

                    {canEdit && (
                      <button
                        onClick={() => setEditingIntegration(integration.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    )}

                    {integration.id === 'microsoft' && (
                      <>
                        {integration.connected ? (
                          <button
                            onClick={() => disconnectMicrosoft.mutate()}
                            disabled={disconnectMicrosoft.isPending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                          >
                            Disconnect
                          </button>
                        ) : integration.configured ? (
                          <button
                            onClick={() => connectMicrosoft.mutate()}
                            disabled={connectMicrosoft.isPending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                          >
                            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                            Connect
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Other Settings */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Other Settings</h2>
        </div>
        <div className="divide-y divide-gray-200">
          <Link
            to="/settings/allowlist"
            className="px-4 py-4 sm:px-6 flex items-center justify-between hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">Customer Allowlist</p>
                <p className="text-sm text-gray-500">Control which customers can submit orders</p>
              </div>
            </div>
            <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-400" />
          </Link>
        </div>
      </div>

      {/* Edit Modal */}
      {editingIntegration && integrationFields[editingIntegration] && (
        <EditModal
          integration={editingIntegration.charAt(0).toUpperCase() + editingIntegration.slice(1)}
          fields={integrationFields[editingIntegration]}
          currentValues={getConfigValues(editingIntegration)}
          onSave={(values) => updateConfig.mutate({ integration: editingIntegration, values })}
          onClose={() => setEditingIntegration(null)}
          isLoading={updateConfig.isPending}
        />
      )}
    </div>
  );
}
